import { ArrowBackIosNew, ArrowForwardIos, LocationOnOutlined } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { numberWithCommas } from '../../MicrospecDynamicUI/Helpers/dataUtils';

// ─── Property Card ───────────────────────────────────────────────────────────
const PropertyCard = ({ property, categories = [] }) => {
  const navigate = useNavigate();
  const category = categories.find((c) => c.id === property.category_id);
  const [currentIndex, setCurrentIndex] = useState(0);

  const images =
    property.images && property.images.length > 0
      ? property.images
      : ['/static/images/placeholder-property.jpg'];

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <Card
        onClick={() => navigate(`/property/${property.id}`)}
        sx={{
          cursor: 'pointer',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            '&:hover .slider-nav': { opacity: 1 },
          }}
        >
          <Box
            component="img"
            src={images[currentIndex]}
            alt={property.title}
            sx={{ width: '100%', height: 300, objectFit: 'cover', display: 'block' }}
          />

          {images.length > 1 && (
            <>
              <IconButton
                className="slider-nav"
                onClick={handleNext}
                size="small"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.85)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  boxShadow: 1,
                  width: 28,
                  height: 28,
                }}
              >
                <ArrowBackIosNew sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton
                className="slider-nav"
                onClick={handlePrev}
                size="small"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.85)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  boxShadow: 1,
                  width: 28,
                  height: 28,
                }}
              >
                <ArrowForwardIos sx={{ fontSize: 14 }} />
              </IconButton>

              {/* Dots */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 0.5,
                }}
              >
                {images.map((_, i) => (
                  <Box
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(i);
                    }}
                    sx={{
                      width: i === currentIndex ? 16 : 6,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </Box>
            </>
          )}

          {category && (
            <Chip
              label={category.name}
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                bgcolor: 'rgba(255,255,255,0.9)',
                fontWeight: 600,
                backdropFilter: 'blur(4px)',
              }}
            />
          )}
        </Box>

        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom noWrap>
            {property.title}
          </Typography>

          {(property.address_display_name || property.address) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, color: 'text.secondary' }}>
              <LocationOnOutlined sx={{ fontSize: 18 }} />
              <Typography variant="body2" noWrap>
                {property.address_display_name || property.address}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant="h6"
              fontWeight={800}
              color="primary.main"
              sx={{ fontSize: '1.1rem' }}
            >
              {property.price_on_request
                ? 'السعر عند الطلب'
                : `${numberWithCommas(property.price)} ₪`}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ─── Property Card Skeleton ──────────────────────────────────────────────────
const PropertyCardSkeleton = () => (
  <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
    <Skeleton variant="rectangular" height={300} />
    <CardContent sx={{ p: 2.5 }}>
      <Skeleton variant="text" width="80%" height={28} />
      <Skeleton variant="text" width="60%" height={20} sx={{ mt: 1 }} />
      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Skeleton variant="text" width="40%" height={28} />
      </Box>
    </CardContent>
  </Card>
);

export { PropertyCard, PropertyCardSkeleton };
export default PropertyCard;
